import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  Keyboard,
  Layers,
  RotateCcw,
  NotebookPen,
  XCircle,
} from "@/lib/icons";
import AppHeader from "@/components/AppHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import GhsPictogramIcon from "@/games/labSafety/GhsPictogramIcon";
import {
  answerMatches,
  getDeck,
  type Deck,
  type FlashCard,
} from "@/learn/decks";
import {
  applyGrade,
  loadProgress,
  pickSession,
  saveProgress,
  type DeckProgress,
  type LeitnerGrade,
} from "@/learn/leitner";
import { loadMissedCards } from "@/learn/missedFromPlay";
import Main from "@/components/Main";

const SESSION_SIZE = 12;

type Mode = "flip" | "type";

/**
 * Study session for a single deck. Two modes:
 *
 *   - Flip: front of card -> tap reveal -> rate yourself
 *     (Again / Hard / Good / Easy). Self-rated, classic Anki.
 *   - Type: type the answer; engine grades it. Correct = "Good",
 *     wrong = "Again". A "Show answer" escape lets you give up cleanly
 *     (recorded as "Again" so it surfaces again soon).
 *
 * Session is built from due cards first, padded from lowest-box rest if
 * fewer than `SESSION_SIZE` are due. When the queue empties, a recap
 * screen shows correct/missed counts and offers another round.
 */
export default function StudySession() {
  const { deckId = "" } = useParams<{ deckId: string }>();
  const navigate = useNavigate();
  const missedCards = useMemo(loadMissedCards, []);
  const deck = useMemo<Deck | null>(() => getDeck(deckId, missedCards), [deckId, missedCards]);

  if (!deck) {
    return (
      <div className="min-h-screen bg-transparent">
        <AppHeader />
        <Main className="max-w-[700px] mx-auto px-4 py-10 text-center">
          <h1 className="text-xl font-semibold text-foreground mb-2">Deck not found</h1>
          <p className="text-sm text-muted-foreground mb-6">
            The deck "{deckId}" doesn't exist.
          </p>
          <Button onClick={() => navigate("/learn")}>Back to Learn</Button>
        </Main>
      </div>
    );
  }

  return <SessionInner deck={deck} />;
}

function SessionInner({ deck }: { deck: Deck }) {
  const [mode, setMode] = useState<Mode>(deck.allowType ? "flip" : "flip");
  const [progress, setProgress] = useState<DeckProgress>(() => loadProgress(deck.id));
  const [queue, setQueue] = useState<string[]>(() =>
    pickSession(deck.cards.map((c) => c.id), loadProgress(deck.id), SESSION_SIZE),
  );
  const [pos, setPos] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [typed, setTyped] = useState("");
  const [typeFeedback, setTypeFeedback] = useState<"correct" | "wrong" | null>(null);
  const [stats, setStats] = useState({ correct: 0, missed: 0 });
  const inputRef = useRef<HTMLInputElement | null>(null);

  const cardId = queue[pos];
  const card: FlashCard | undefined = useMemo(
    () => deck.cards.find((c) => c.id === cardId),
    [deck, cardId],
  );

  // Focus input when entering Type mode for a new card.
  useEffect(() => {
    if (mode === "type" && !revealed) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [mode, revealed, cardId]);

  const persist = useCallback(
    (next: DeckProgress) => {
      setProgress(next);
      saveProgress(deck.id, next);
    },
    [deck.id],
  );

  const advance = useCallback(() => {
    setRevealed(false);
    setTyped("");
    setTypeFeedback(null);
    setPos((p) => p + 1);
  }, []);

  const grade = useCallback(
    (g: LeitnerGrade) => {
      if (!card) return;
      const nextEntry = applyGrade(progress[card.id], g);
      const nextProgress: DeckProgress = { ...progress, [card.id]: nextEntry };
      persist(nextProgress);
      setStats((s) => ({
        correct: s.correct + (g === "again" ? 0 : 1),
        missed: s.missed + (g === "again" ? 1 : 0),
      }));
      advance();
    },
    [card, progress, persist, advance],
  );

  const restart = useCallback(() => {
    const fresh = loadProgress(deck.id);
    setProgress(fresh);
    setQueue(pickSession(deck.cards.map((c) => c.id), fresh, SESSION_SIZE));
    setPos(0);
    setRevealed(false);
    setTyped("");
    setTypeFeedback(null);
    setStats({ correct: 0, missed: 0 });
  }, [deck]);

  // ---------------- recap ----------------

  const finished = pos >= queue.length;
  if (finished) {
    return (
      <SessionShell deck={deck} mode={mode} setMode={setMode}>
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <NotebookPen size={28} className="mx-auto text-primary mb-2" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">Session complete</p>
          <p className="text-3xl font-bold font-mono text-foreground tabular-nums mt-1">
            {stats.correct} / {queue.length}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {stats.missed} marked for review.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            <Button onClick={restart}>Study again</Button>
            <Button variant="outline" asChild>
              <Link to="/learn">Back to Learn</Link>
            </Button>
          </div>
        </div>
      </SessionShell>
    );
  }

  if (!card) return null;

  // ---------------- card ----------------

  const isPictogram = card.kind === "pictogram" && card.pictogram;

  const handleType = (e: React.FormEvent) => {
    e.preventDefault();
    if (typeFeedback) return;
    const ok = answerMatches(typed, card);
    setTypeFeedback(ok ? "correct" : "wrong");
    setRevealed(true);
    if (ok) {
      grade("good");
    }
    // Wrong: wait for the user to read the answer, then they tap "Got it"
    // to record an "again" and move on.
  };

  const handleTypeContinue = () => {
    grade("again");
  };

  return (
    <SessionShell deck={deck} mode={mode} setMode={setMode}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground tabular-nums font-mono">
          Card {pos + 1} / {queue.length}
        </p>
        <p className="text-[11px] text-muted-foreground">
          <span className="font-mono">{stats.correct}</span> right ·{" "}
          <span className="font-mono">{stats.missed}</span> missed
        </p>
      </div>

      <article
        className="rounded-xl border border-border bg-card p-6 min-h-[260px] flex flex-col"
        aria-live="polite"
      >
        {/* Front */}
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 py-4">
          {isPictogram ? (
            <GhsPictogramIcon symbol={card.pictogram!} size={120} />
          ) : (
            <p className="text-3xl font-mono font-semibold text-foreground break-words">
              {card.prompt}
            </p>
          )}
          {!revealed && card.hint && mode === "flip" && (
            <p className="text-xs text-muted-foreground max-w-prose">{card.hint}</p>
          )}
        </div>

        {/* Back / answer */}
        {revealed && (
          <div className="border-t border-border pt-4 mt-4 text-center">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
              Answer
            </p>
            <p className="text-xl font-semibold text-foreground">{card.answer}</p>
            {card.hint && (
              <p className="text-xs text-muted-foreground mt-2 max-w-prose mx-auto">
                {card.hint}
              </p>
            )}
          </div>
        )}
      </article>

      {/* Controls */}
      {mode === "flip" ? (
        <div className="mt-4">
          {!revealed ? (
            <div className="flex gap-2">
              <Button onClick={() => setRevealed(true)} className="flex-1">
                <Eye size={14} className="mr-1.5" aria-hidden="true" />
                Reveal answer
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <GradeButton label="Again" sub="< 1 min" onClick={() => grade("again")} variant="destructive" />
              <GradeButton label="Hard" sub="same box" onClick={() => grade("hard")} variant="outline" />
              <GradeButton label="Good" sub="next box" onClick={() => grade("good")} variant="secondary" />
              <GradeButton label="Easy" sub="skip ahead" onClick={() => grade("easy")} variant="default" />
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleType} className="mt-4 space-y-3">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Type your answer"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              disabled={!!typeFeedback}
            />
            {!typeFeedback ? (
              <Button type="submit" disabled={typed.trim().length === 0}>
                Check
              </Button>
            ) : (
              <Button type="button" onClick={handleTypeContinue} variant="outline">
                Continue
              </Button>
            )}
          </div>
          {typeFeedback && (
            <div
              className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
                typeFeedback === "correct"
                  ? "border-emerald-500/40 bg-emerald-500/10 text-foreground"
                  : "border-destructive/40 bg-destructive/10 text-foreground"
              }`}
              role="status"
            >
              {typeFeedback === "correct" ? (
                <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" aria-hidden="true" />
              ) : (
                <XCircle size={16} className="text-destructive mt-0.5 shrink-0" aria-hidden="true" />
              )}
              <span>
                {typeFeedback === "correct"
                  ? "Nice - logged as Good."
                  : `Not quite. Answer: ${card.answer}`}
              </span>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            Type mode is strict on spelling but ignores case and punctuation. Aliases like a symbol or atomic number are accepted where listed.
          </p>
        </form>
      )}
    </SessionShell>
  );
}

// ---------------- shell ----------------

function SessionShell({
  deck,
  mode,
  setMode,
  children,
}: {
  deck: Deck;
  mode: Mode;
  setMode: (m: Mode) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-transparent">
      <AppHeader />
      <Main className="max-w-[700px] mx-auto px-4 py-6">
        <Link
          to="/learn"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <ArrowLeft size={12} aria-hidden="true" />
          Back to Learn
        </Link>
        <header className="mb-5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Layers size={16} className="text-primary" aria-hidden="true" />
              <h1 className="text-2xl font-bold text-foreground truncate">{deck.title}</h1>
            </div>
            <p className="text-sm text-muted-foreground">{deck.description}</p>
          </div>
          {deck.allowType && (
            <div
              role="tablist"
              aria-label="Study mode"
              className="flex items-center rounded-md border border-border bg-card p-0.5 shrink-0"
            >
              <ModeTab
                active={mode === "flip"}
                onClick={() => setMode("flip")}
                icon={<RotateCcw size={12} aria-hidden="true" />}
                label="Flip"
              />
              <ModeTab
                active={mode === "type"}
                onClick={() => setMode("type")}
                icon={<Keyboard size={12} aria-hidden="true" />}
                label="Type"
              />
            </div>
          )}
        </header>
        {children}
      </Main>
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function GradeButton({
  label,
  sub,
  onClick,
  variant,
}: {
  label: string;
  sub: string;
  onClick: () => void;
  variant: "default" | "outline" | "secondary" | "destructive";
}) {
  return (
    <Button onClick={onClick} variant={variant} className="flex-col h-auto py-2.5 gap-0.5">
      <span className="text-sm font-semibold">{label}</span>
      <span className="text-[10px] opacity-80">{sub}</span>
    </Button>
  );
}